import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { copyToClipboard, cx, pack } from "@/utils";
import FauxInput from "@components/Field/Base/Faux";

import DefaultItem from "./Item";
import { TokenizedInputProps } from "./types";

import styles from "./TokenizedInput.module.css";

/**
 * TokenizedInput - allow the user to add multiple values to a list
 * that mimics the look of a regular input.
 *
 * - className: Optional class for styling the component.
 * - onChange: Function to call when the select value changes.
 * - validateItem: Function for custom validation logic. Uses the same type of validators as FinalForm does.
 * - validatePaste: Whether or not to validate pasted content.
 * - commitOnAbandon: Whether to commit the draft value when focus is lost.
 * - placeholder: Placeholder text for the input.
 * - rootRef: Ref to the underlying FauxInput component.
 * - ItemComponent: The component to render for each item in the list.
 * - value: The initial values.
 */
const TokenizedInput = forwardRef<HTMLInputElement, TokenizedInputProps>(
  (
    {
      className,
      onChange,
      validateItem,
      validatePaste = true,
      commitOnAbandon = true,
      placeholder,
      rootRef,
      ItemComponent = DefaultItem,
      value: initialValues = [],
      ...rest
    },
    ref
  ) => {
    // Value represented by array of strings
    const [values, setValues] = useState<string[]>(initialValues);

    // The current string being edited
    const [draft, setDraft] = useState("");

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Keep internal state in sync with the "above" state
    useEffect(() => {
      onChange && onChange(values);
    }, [values, onChange]);

    // Clean out unwanted values before commiting
    const clean = (newValue: string) => {
      // Prevent empty
      if (newValue == "") {
        return false;
      }
      // Prevent duplicates
      if ((values || []).includes(newValue)) {
        return false;
      }
      return true;
    };

    const sanitize = (newValue: string) => newValue.trim();

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedValue = e.clipboardData?.getData("text");

      // Not much to do if there's no value. early return.
      if (pastedValue === undefined) return;

      let pastedValues = pastedValue.split(",");

      if (pastedValues.length > 1) {
        // Perform basic sanitization
        pastedValues = pastedValues.filter(clean).map(sanitize);

        // Perform user defined validation
        if (validatePaste && validateItem) {
          pastedValues = pastedValues.filter(
            (v) => validateItem?.(v) == undefined
          );
        }

        setValues((v) => [...v, ...pastedValues]);
      } else {
        // No need for any sanitization since this is still a draft
        setDraft((v) => (v ? pastedValue : `${v} ${pastedValue}`));
      }
    };

    // User has commited to the draft, let's validate and save it if it's OK. Otherwise error out.
    const commitDraft = (
      e:
        | React.KeyboardEvent<HTMLInputElement>
        | React.FocusEvent<HTMLInputElement>
    ) => {
      e.preventDefault();
      const inputValue = e.currentTarget.value;
      // Make sure this is not a double of something that's already there.
      const existing = values && values.find((v) => v === inputValue);
      // Nothing to save?
      if (!inputValue || existing) {
        return;
      }

      // Save the values,
      setValues((v) => [...v, inputValue]);
      setDraft("");
    };

    // User has no commited draft and  there's a previous value. Remove it.
    const handleValueRemove = () => {
      if (draft === "") {
        setValues((v) => {
          if (v.length > 1) {
            const p = [...v];
            p.pop();
            return p;
          } else {
            return [];
          }
        });
      }
    };

    // User triggered the remove function from an item.
    const handleItemRemove = (index: number) => {
      setValues((v) => {
        const newValues = [...v];
        newValues.splice(index, 1);
        return newValues.filter(Boolean);
      });
    };

    const handleItemEdit = (index: number) => {
      setDraft(values[index]);
      handleItemRemove(index);
    };

    // User triggered keyup within the draft input.
    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        default:
          break;
        case "Enter":
          commitDraft(e);
          break;
      }
    };

    // User triggered keydown within the draft input.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        default:
          break;
        // Prevent enter when commiting a value to submit the surrounding form.
        // However when a draft is not present, this behaviour is expected.
        case "Enter":
          if (draft) e.preventDefault();
          break;
        case "Backspace":
          handleValueRemove();
          break;
      }
    };

    // User has left the input, let's commit the draft if commitOnAbandon is active.
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      commitOnAbandon && commitDraft(e);
    };

    // User has tried to copy something from the input
    const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
      // Checking for an empty draft here might be unnecessary, but I hope it can prevent
      // some weird edge cases where a user tries to copy something from the draft input
      if (e.currentTarget.value === "") {
        copyToClipboard(pack(values).join(","));
      }
    };

    return (
      <FauxInput
        {...rest}
        ref={rootRef}
        className={cx(styles.root, className)}
        onClick={() => inputRef.current?.focus()}
      >
        {(values || []).map((v, i) => {
          // If any, loop over the validators, undefined means everything is OK
          const errors = validateItem?.(v);

          return (
            <ItemComponent
              onEdit={() => handleItemEdit(i)}
              onRemove={() => handleItemRemove(i)}
              errorMessage={errors}
              key={i}
              value={v}
            />
          );
        })}

        <input
          placeholder={values.length === 0 ? placeholder : ""}
          ref={inputRef}
          className={styles.input}
          onBlur={handleBlur}
          onChange={(e) => setDraft(e.target.value)}
          value={draft}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onCopy={handleCopy}
        />
      </FauxInput>
    );
  }
);
TokenizedInput.displayName = "TokenizedInput";

export default TokenizedInput;
